import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useContext, useEffect } from "react";
import { useForm } from "react-hook-form";
import { DialogProvider, createDialogContext } from "../../Common/Dialog";
import { KYDEventBundle } from "@common/interfaces/KYDEvent";

export const AddEditBundleDialogContext = createDialogContext();

const AddEditBundleDialog = ({
  onSave,
  onClose,
  bundle,
  isLoading = false,
}: {
  bundle?: KYDEventBundle;
  isLoading: boolean;
  onClose: () => void;
  onSave: (data: { title: string }) => Promise<void>;
}) => {
  const { isOpen } = useContext(AddEditBundleDialogContext);

  const {
    handleSubmit,
    reset,
    register,
    formState: { isValid },
  } = useForm({
    mode: "onChange",
    criteriaMode: "all",
    reValidateMode: "onChange",
    values: {
      title: bundle?.title || "",
    },
  });

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen]);

  const _onClose = () => {
    reset();
    onClose();
  };

  return (
    <>
      <Modal
        closeOnOverlayClick={false}
        isOpen={isOpen}
        onClose={_onClose}
        isCentered
      >
        <ModalOverlay />
        <ModalContent
          mb={[0, "auto"]}
          borderBottomLeftRadius={[0, "md"]}
          borderBottomRightRadius={[0, "md"]}
        >
          <ModalHeader paddingTop={4}>
            <VStack gap={0} width={"full"}>
              <Text w="full">{bundle ? "Edit bundle" : "Create a bundle"}</Text>
            </VStack>
          </ModalHeader>
          <ModalBody textAlign={"left"}>
            <VStack w="full">
              <Text w="full">Title</Text>
              <Input {...register("title")} />
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Stack direction={["column-reverse", "row-reverse"]} width="100%">
              <Button
                key={"cancel"}
                isLoading={isLoading}
                isDisabled={isLoading}
                w={"auto"}
                onClick={_onClose}
              >
                Cancel
              </Button>
              <Button
                key={"submit"}
                variant="kydDark"
                w={"auto"}
                isLoading={isLoading}
                isDisabled={isLoading || !isValid}
                onClick={handleSubmit((data) => onSave(data))}
              >
                Save
              </Button>
            </Stack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export const withAddEditBundleDialog =
  <T extends object>(Component: React.ComponentType<T>) =>
  (props: T) => {
    return (
      <DialogProvider context={AddEditBundleDialogContext}>
        <Component {...(props as T)} />
      </DialogProvider>
    );
  };

export default AddEditBundleDialog;
